import { useCallback } from 'react';

import { useQueries } from '@tanstack/react-query';

import { integrationsEndpoints as endpoints } from '@/api/endpoints';
import useDesks from '@/hooks/useDesks';
import { API } from '@/hooks/useIntegrations';
import { Integration, Integrations } from '@/shared/types/integration';

const ONE_MINUTE = 60 * 1000;

type CombineResult = { data?: Integrations; isLoading: boolean };

/**
 * Lists every connection (integration) across all environments. There is no account-level
 * integrations endpoint — it is scoped per desk — so we fan out one query per desk and merge,
 * de-duplicating by `integration_id`. Mirrors the per-desk fan-out in `useAllRules`.
 */
export const useAllConnections = () => {
  const { desks } = useDesks();

  // Stable `combine` identity so React Query memoizes the merged result — otherwise the
  // `connections` array gets a new identity every render and cascades through downstream memos.
  const combine = useCallback((results: CombineResult[]) => {
    const byId = new Map<string, Integration>();
    results.forEach((result) => {
      result.data?.integrations?.forEach((integration) => {
        if (!byId.has(integration.integration_id)) {
          byId.set(integration.integration_id, integration);
        }
      });
    });

    return {
      connections: Array.from(byId.values()),
      // Loading until every desk's integrations have resolved (or there are no desks).
      isLoading: results.length > 0 && results.some((result) => result.isLoading),
    };
  }, []);

  return useQueries({
    queries: (desks ?? []).map((desk) => ({
      queryKey: [endpoints.integrations(desk.desk_id)],
      queryFn: () => API.listIntegrations(desk.desk_id),
      enabled: Boolean(desk.desk_id),
      staleTime: ONE_MINUTE,
    })),
    combine,
  });
};
